import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Leaf } from "@phosphor-icons/react";
import { PACK_IMAGE, HERO_IMAGE } from "@/data/botanicals";
import { scrollToSection } from "@/components/site/Navbar";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.2, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { value: "0%", label: "Nicotine" },
  { value: "0%", label: "Tobacco" },
  { value: "10", label: "Sacred Botanicals" },
  { value: "100%", label: "Organic" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const packY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden flex items-end">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img src={HERO_IMAGE} alt="Botanical still life" className="w-full h-[115%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#062516]/80 via-[#062516]/55 to-[#062516]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 pt-40 pb-16 lg:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
              className="flex items-center gap-3 mb-8"
            >
              <Leaf size={18} weight="duotone" className="text-[#C5A880]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[#C5A880]">
                The tobacco-free, nicotine-free revolution
              </span>
            </motion.div>

            <h1 data-testid="hero-headline" className="font-display text-[#F5F1E8] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              <MaskedLine delay={0.45}>Ancient Wisdom,</MaskedLine>
              <MaskedLine delay={0.6} className="italic text-[#C5A880]">
                Modern Ritual.
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.95, ease: EASE }}
              className="mt-8 max-w-xl text-[#F5F1E8]/75 text-base md:text-lg leading-relaxed font-light"
            >
              Sage & Spirit crafts luxury herbal cigarettes from ten sacred Ayurvedic
              botanicals — a ceremonial smoke with nothing to hide and nothing to hook you.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.1, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-5"
            >
              <button
                data-testid="hero-explore-button"
                onClick={() => scrollToSection("#botanicals")}
                className="group rounded-full bg-[#C5A880] text-[#062516] px-8 py-4 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#D9BE93] transition-colors duration-300 flex items-center gap-3"
              >
                Explore the Botanicals
                <ArrowDown size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-y-1" />
              </button>
              <button
                data-testid="hero-partner-button"
                onClick={() => scrollToSection("#partners")}
                className="rounded-full border border-[#F5F1E8]/30 text-[#F5F1E8] px-8 py-4 text-sm uppercase tracking-[0.15em] hover:border-[#C5A880] hover:text-[#C5A880] transition-colors duration-300"
              >
                Global Distribution
              </button>
            </motion.div>
          </div>

          <div className="lg:col-span-4 hidden lg:flex justify-end">
            <motion.div
              style={{ y: packY }}
              initial={{ opacity: 0, y: 60, rotate: 4 }}
              animate={{ opacity: 1, y: 0, rotate: 0 }}
              transition={{ duration: 1.4, delay: 0.8, ease: EASE }}
              className="relative"
            >
              <div className="absolute -inset-8 rounded-full bg-[#C5A880]/15 blur-3xl" />
              <motion.img
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                src={PACK_IMAGE}
                alt="Sage & Spirit pack"
                data-testid="hero-pack-image"
                className="relative w-64 xl:w-72 rounded-lg shadow-2xl shadow-black/50"
              />
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-16 pt-8 border-t border-[#F5F1E8]/10 grid grid-cols-2 md:grid-cols-4 gap-8"
          data-testid="hero-stats"
        >
          {STATS.map((s, i) => (
            <div key={s.label} className="flex flex-col gap-1">
              <span className="font-display text-3xl md:text-4xl text-[#C5A880]">{s.value}</span>
              <span className="text-xs uppercase tracking-[0.2em] text-[#F5F1E8]/55">{s.label}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
